import {
  Attachment,
  IntakeRequest,
  IntakeRequestType,
  IntakeRequestTypeAdoptSsoApp,
  IntakeRequestTypeIntegrateApi,
  IntakeRequestTypeOnBoardIdp,
  IntakeRequestTypeSsoConnector,
  RequestStatus,
} from '../types';
import {
  IntakeRequestListItemApi,
  IntakeRequestStatusApi,
  IntakeAttachmentApi,
  IntegrationRequestDetailsApi,
  SsoConnectorRequestDetailsApi,
} from './apiTypes';

const INTAKE_REQUEST_STATUS: Record<RequestStatus, IntakeRequestStatusApi> = {
  New: 'NEW',
  'In progress': 'IN_PROGRESS',
  Received: 'RECEIVED',
  Resolved: 'RESOLVED',
  Reopened: 'REOPENED',
  Canceled: 'CANCELED',
};

function buildIntakeRequestStatusUiMap(): Record<
  IntakeRequestStatusApi,
  RequestStatus
> {
  const map: Partial<Record<IntakeRequestStatusApi, RequestStatus>> = {};

  Object.entries(INTAKE_REQUEST_STATUS).forEach(([uiStatus, apiStatus]) => {
    map[apiStatus] = uiStatus as RequestStatus;
  });

  return map as Record<IntakeRequestStatusApi, RequestStatus>;
}

const INTAKE_REQUEST_STATUS_UI = buildIntakeRequestStatusUiMap();

const INTEGRATION_TYPES = new Set<IntakeRequestType>([
  IntakeRequestType.OnBoardIdp,
  IntakeRequestType.AdoptSsoApp,
  IntakeRequestType.IntegrateApi,
]);

const SSO_TYPES = new Set<IntakeRequestType>([
  IntakeRequestType.Oidc,
  IntakeRequestType.Saml,
]);

const INTAKE_TYPE_ALIASES: Record<string, IntakeRequestType> = {
  [IntakeRequestType.OnBoardIdp]: IntakeRequestType.OnBoardIdp,
  'onboard idp to dxm sso': IntakeRequestType.OnBoardIdp,
  [IntakeRequestType.AdoptSsoApp]: IntakeRequestType.AdoptSsoApp,
  'adopt dxm sso in the application': IntakeRequestType.AdoptSsoApp,
  [IntakeRequestType.IntegrateApi]: IntakeRequestType.IntegrateApi,
  'integrate api into dxm gateway': IntakeRequestType.IntegrateApi,
  [IntakeRequestType.Oidc]: IntakeRequestType.Oidc,
  [IntakeRequestType.Saml]: IntakeRequestType.Saml,
};

function mapIntakeStatusToUi(status: IntakeRequestStatusApi): RequestStatus {
  return INTAKE_REQUEST_STATUS_UI[status];
}

function mapRequestStatusToApi(status: RequestStatus): IntakeRequestStatusApi {
  return INTAKE_REQUEST_STATUS[status];
}

function toStringArray(value: unknown): string[] {
  if (typeof value === 'string') return value ? [value] : [];

  return Array.isArray(value)
    ? value.filter(
        (item): item is string => typeof item === 'string' && item.length > 0,
      )
    : [];
}

function pickFirstString(
  value: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
  }

  return undefined;
}

function extractMixedItemLabel(item: unknown): string {
  if (typeof item === 'string') return item;
  if (typeof item !== 'object' || item === null) return '';

  return (
    pickFirstString(item as Record<string, unknown>, [
      'url',
      'uri',
      'name',
      'fileName',
    ]) || ''
  );
}

function parseDataUrlFileName(value: string): string | undefined {
  if (!value.startsWith('data:')) return undefined;

  const nameMatch = value.match(/;name=([^;]+)/i);
  if (!nameMatch) return undefined;

  try {
    return decodeURIComponent(nameMatch[1]);
  } catch {
    return nameMatch[1];
  }
}

function toDisplayFileName(value: string): string {
  return parseDataUrlFileName(value) || value || 'Attached spec file';
}

function toStringArrayFromMixedItems(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return value.map(extractMixedItemLabel).filter(label => Boolean(label));
}

function toStringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function toStringOrJoined(value: unknown): string {
  return toStringArray(value).join(', ');
}

function toBooleanOrFalse(value: unknown): boolean {
  return typeof value === 'boolean' ? value : false;
}

function parseRawConfig(rawConfig: unknown): Record<string, unknown> {
  const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

  if (!rawConfig) return {};
  if (typeof rawConfig === 'string') {
    try {
      const parsed = JSON.parse(rawConfig);
      if (isRecord(parsed)) return parsed;
      return {};
    } catch {
      return {};
    }
  }
  if (isRecord(rawConfig)) return rawConfig;
  return {};
}

function normalizeTypeCandidate(value?: string): string {
  if (!value) return '';
  return value.trim().toLowerCase();
}

export function normalizeIntakeRequestType(
  value?: string,
): IntakeRequestType | undefined {
  const typeValue = normalizeTypeCandidate(value);
  return INTAKE_TYPE_ALIASES[typeValue];
}

export function isIntegrationIntakeType(
  requestType: IntakeRequestType,
): boolean {
  return INTEGRATION_TYPES.has(requestType);
}

export function isSsoIntakeType(requestType: IntakeRequestType): boolean {
  return SSO_TYPES.has(requestType);
}

export function resolveIntakeRequestController(
  type: string,
): 'integration' | 'sso' {
  const normalizedType = normalizeIntakeRequestType(type);
  if (!normalizedType)
    throw new Error(`Unsupported intake request type: ${type}`);
  if (isIntegrationIntakeType(normalizedType)) return 'integration';
  if (isSsoIntakeType(normalizedType)) return 'sso';
  throw new Error(`Unable to resolve controller for type: ${type}`);
}

function mapRequestTypeDetails(
  requestType: IntakeRequestType,
  rawConfig: unknown,
):
  | IntakeRequestTypeOnBoardIdp
  | IntakeRequestTypeAdoptSsoApp
  | IntakeRequestTypeIntegrateApi
  | IntakeRequestTypeSsoConnector {
  const config = parseRawConfig(rawConfig);

  if (requestType === IntakeRequestType.OnBoardIdp) {
    return {
      type: IntakeRequestType.OnBoardIdp,
      appName: toStringOrEmpty(config.appName),
      idpType: toStringOrEmpty(config.idpType),
      contactEmails: toStringArray(config.contactEmails),
    };
  }

  if (requestType === IntakeRequestType.AdoptSsoApp) {
    return {
      type: IntakeRequestType.AdoptSsoApp,
      appName: toStringOrEmpty(config.appName),
      appType: toStringOrEmpty(config.appType),
    };
  }

  if (
    requestType === IntakeRequestType.Oidc ||
    requestType === IntakeRequestType.Saml
  ) {
    return {
      type: requestType,
      issuer: toStringOrEmpty(config.issuer),
      clientId: toStringOrEmpty(config.clientId),
      clientSecret: toStringOrEmpty(config.clientSecret),
    };
  }

  return {
    type: IntakeRequestType.IntegrateApi,
    apiBaseUrls:
      toStringOrJoined(config.apiBaseUrls) ||
      toStringOrJoined(config.apiServerUris),
    apiSpecUris: [
      ...toStringArray(config.apiSpecUris),
      ...toStringArray(config.openapiSpecUris),
    ],
    apiSpecFiles: toStringArrayFromMixedItems(config.apiSpecFiles).map(
      toDisplayFileName,
    ),
    additionalNotes:
      toStringOrEmpty(config.additionalNotes) || toStringOrEmpty(config.notes),
    gatewayBasePath: toStringOrEmpty(config.gatewayBasePath),
    apiSpecVisibilityScope: toStringOrEmpty(config.apiSpecVisibilityScope),
    corsConfiguration: toStringOrEmpty(config.corsConfiguration),
    environmentMapping: toStringOrEmpty(config.environmentMapping),
    authenticationDetails:
      toStringOrEmpty(config.authenticationDetails) ||
      toStringOrEmpty(config.authorizationDetails),
    tokenValidationLocation: toStringOrEmpty(config.tokenValidationLocation),
    currentAuthorizationApproach: toStringOrEmpty(
      config.currentAuthorizationApproach,
    ),
    dxmAuthorizationLayer: toBooleanOrFalse(config.dxmAuthorizationLayer),
    exposeApiViaGateway: toBooleanOrFalse(config.exposeApiViaGateway),
    integrationType: toStringOrEmpty(config.integrationType),
    loadTestingConsent: toBooleanOrFalse(config.loadTestingConsent),
    responsibleContacts:
      toStringOrEmpty(config.responsibleContacts) ||
      toStringArray(config.teamContacts).join(', '),
    supportTeamEmails: toStringArray(config.supportTeamEmails),
    apiServerUris: toStringArray(config.apiServerUris),
    apiDocUris: toStringArray(config.apiDocUris),
  };
}

function mapTicketType(requestType: IntakeRequestType): string {
  return isIntegrationIntakeType(requestType)
    ? 'Integration Request'
    : 'SSO Request';
}

interface IntakeRequestSource {
  id: string;
  requestId: string;
  username: string;
  email: string;
  status: IntakeRequestStatusApi;
  resolutionComment: string | null;
  type: string;
  config: unknown;
  createdDate: string;
  lastModifiedDate: string | null;
  assignedAdmin?: string | null;
}

function mapCoreIntakeRequest(params: IntakeRequestSource): IntakeRequest {
  const normalizedType =
    normalizeIntakeRequestType(params.type) ?? IntakeRequestType.OnBoardIdp;

  return {
    id: params.id,
    requestId: params.requestId,
    submittedAt: params.createdDate,
    requestType: normalizedType,
    name: params.username,
    email: params.email,
    status: mapIntakeStatusToUi(params.status),
    resolutionComment: params.resolutionComment,
    assignedAdmin: params.assignedAdmin || null,
    description: '',
    ticketType: mapTicketType(normalizedType),
    createDate: params.createdDate,
    updatedAt: params.lastModifiedDate,
    attachments: [],
    requestTypeDetails: mapRequestTypeDetails(normalizedType, params.config),
  };
}

function mapIntakeRequestFromSource(
  params: IntakeRequestSource,
): IntakeRequest {
  const config = parseRawConfig(params.config);

  return mapCoreIntakeRequest({
    ...params,
    config: {
      ...config,
      type: params.type,
    },
  });
}

export function mapIntakeRequestListItem(
  api: IntakeRequestListItemApi,
): IntakeRequest {
  return mapIntakeRequestFromSource({
    id: api.id,
    requestId: api.requestId,
    username: api.username,
    email: api.email,
    status: api.status,
    resolutionComment: api.resolutionComment,
    type: api.type,
    config: api.config,
    createdDate: api.createdDate,
    lastModifiedDate: api.lastModifiedDate,
  });
}

export function mapIntegrationRequestDetails(
  api: IntegrationRequestDetailsApi,
): IntakeRequest {
  const derivedType =
    normalizeIntakeRequestType(api.config.integrationType) ??
    normalizeIntakeRequestType(api.type) ??
    IntakeRequestType.OnBoardIdp;

  return mapIntakeRequestFromSource({
    id: api.id,
    requestId: api.requestId,
    username: api.username,
    email: api.email,
    status: api.status,
    resolutionComment: api.resolutionComment,
    type: derivedType,
    config: api.config,
    createdDate: api.createdDate,
    lastModifiedDate: api.lastModifiedDate,
    assignedAdmin: api.assignedAdmin,
  });
}

export function mapSsoConnectorRequestDetails(
  api: SsoConnectorRequestDetailsApi,
): IntakeRequest {
  const derivedType =
    normalizeIntakeRequestType(api.type) ??
    normalizeIntakeRequestType(api.config.type) ??
    IntakeRequestType.Oidc;

  return mapIntakeRequestFromSource({
    id: api.id,
    requestId: api.requestId,
    username: api.username || api.name,
    email: api.email,
    status: api.status,
    resolutionComment: api.resolutionComment,
    type: derivedType,
    config: api.config,
    createdDate: api.createdDate,
    lastModifiedDate: api.lastModifiedDate,
    assignedAdmin: api.assignedAdmin,
  });
}

export function mapIntakeRequestStatusToApi(
  status: RequestStatus,
): IntakeRequestStatusApi {
  return mapRequestStatusToApi(status);
}

export function mapIntakeStatusToApi(
  value: string,
): IntakeRequestStatusApi | undefined {
  const isRequestStatus = (status: string): status is RequestStatus =>
    Object.prototype.hasOwnProperty.call(INTAKE_REQUEST_STATUS, status);

  if (!isRequestStatus(value)) return undefined;
  return INTAKE_REQUEST_STATUS[value];
}

export function mapIntakeAttachments(api: IntakeAttachmentApi[]): Attachment[] {
  return api.map(item => ({
    id: item.id,
    name: item.name,
    uploadedAt: item.createdDate,
    url: item.url,
    downloadUrl: item.downloadUrl,
  }));
}
